/*
 * Vouloir micro-interactions (Decision #040 design pass). Restrained,
 * progressive-enhancement scroll reveal: each top-level section fades
 * and rises into view once, the first section on the page is excluded
 * so nothing is invisible on first paint. Respects reduced-motion, and
 * since no CSS ever hides content by default, a stalled reveal degrades
 * to fully-visible rather than staying invisible.
 *
 * Uses a manual scroll/resize sweep instead of IntersectionObserver:
 * this theme scrolls an inner .page-wrapper, not the window/document,
 * and IntersectionObserver's rendering-tied callback can silently stop
 * firing when the tab is backgrounded or occluded, leaving whole
 * sections permanently at opacity:0. A capture-phase scroll listener
 * plus a manual getBoundingClientRect check has no such dependency, and
 * a visibilitychange sweep catches anything missed while hidden.
 */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var sections = Array.prototype.slice.call(
    document.querySelectorAll('main .shopify-section')
  );
  var targets = sections.slice(1);
  if (!targets.length) return;

  targets.forEach(function (el) {
    el.style.opacity = '0';
    el.style.transform = 'translateY(24px)';
    el.style.transition =
      'opacity 900ms cubic-bezier(0.16, 1, 0.3, 1), transform 900ms cubic-bezier(0.16, 1, 0.3, 1)';
  });

  var pending = targets.slice();

  function reveal(el) {
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';
  }

  function sweep() {
    if (!pending.length) return;
    var vh = window.innerHeight;
    pending = pending.filter(function (el) {
      var rect = el.getBoundingClientRect();
      var inView = rect.top < vh * 0.92 && rect.bottom > 0;
      if (inView) reveal(el);
      return !inView;
    });
    if (!pending.length) {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('visibilitychange', sweep);
    }
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      sweep();
      ticking = false;
    });
  }

  sweep();
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onScroll);
  document.addEventListener('visibilitychange', sweep);
})();

/*
 * Story carousel (homepage) — one Book 001 piece at a time. Arrow
 * clicks and touch swipe both move a single `current` index; a shared
 * meta bar (title/chapter/price/CTA) re-reads the active slide's data
 * attributes on every change rather than duplicating that markup per
 * slide. Loops at both ends since there's no natural "last" piece to
 * stop on. No autoplay, per spec.
 */
(function () {
  var root = document.querySelector('.story-carousel');
  if (!root) return;

  var slides = Array.prototype.slice.call(root.querySelectorAll('.story-carousel__slide'));
  if (!slides.length) return;

  var counterEl = root.querySelector('.story-carousel__counter');
  var titleEl = root.querySelector('.story-carousel__title');
  var chapterEl = root.querySelector('.story-carousel__chapter');
  var priceEl = root.querySelector('.story-carousel__price');
  var ctaEl = root.querySelector('.story-carousel__cta');
  var prevBtn = root.querySelector('.story-carousel__arrow--prev');
  var nextBtn = root.querySelector('.story-carousel__arrow--next');
  var viewport = root.querySelector('.story-carousel__viewport');

  var total = slides.length;
  var current = 0;

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function render() {
    slides.forEach(function (slide, i) {
      var isActive = i === current;
      slide.classList.toggle('is-active', isActive);
      if (isActive) {
        slide.removeAttribute('aria-hidden');
        slide.removeAttribute('tabindex');
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide.setAttribute('tabindex', '-1');
      }
    });
    var active = slides[current];
    if (counterEl) counterEl.textContent = pad(current + 1) + ' / ' + pad(total);
    if (titleEl) titleEl.textContent = active.getAttribute('data-title') || '';
    if (chapterEl) chapterEl.textContent = active.getAttribute('data-chapter') || '';
    if (priceEl) priceEl.textContent = active.getAttribute('data-price') || '';
    if (ctaEl) ctaEl.href = active.getAttribute('href') || '#';
  }

  function go(delta) {
    current = (current + delta + total) % total;
    render();
  }

  if (prevBtn) {
    prevBtn.addEventListener('click', function (e) {
      e.preventDefault();
      go(-1);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      go(1);
    });
  }

  if (viewport) {
    var touchStartX = null;
    viewport.addEventListener(
      'touchstart',
      function (e) {
        touchStartX = e.touches[0].clientX;
      },
      { passive: true }
    );
    viewport.addEventListener(
      'touchend',
      function (e) {
        if (touchStartX === null) return;
        var dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
        touchStartX = null;
      },
      { passive: true }
    );
  }

  render();
})();

/*
 * Ambient cursor light — desktop only. Writes the pointer position into
 * two CSS custom properties that assets/vouloir-brand.css reads on a
 * fixed radial-gradient layer; toggles a body class so the glow fades
 * in on first move and fades out when the pointer leaves the window,
 * rather than snapping in at page load pinned to (0,0). rAF-throttled
 * to avoid flooding style recalculation on fast mousemove.
 */
(function () {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!window.matchMedia('(min-width: 990px)').matches) return;
  if (!window.matchMedia('(pointer: fine)').matches) return;

  var ticking = false;
  var lastX = 0;
  var lastY = 0;

  function apply() {
    document.body.style.setProperty('--vouloir-cursor-x', lastX + 'px');
    document.body.style.setProperty('--vouloir-cursor-y', lastY + 'px');
    ticking = false;
  }

  document.addEventListener(
    'pointermove',
    function (e) {
      lastX = e.clientX;
      lastY = e.clientY;
      document.body.classList.add('vouloir-cursor-active');
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(apply);
      }
    },
    { passive: true }
  );

  document.addEventListener('pointerleave', function () {
    document.body.classList.remove('vouloir-cursor-active');
  });
})();
